import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    OcrBatchBox,
    OcrBatchCard,
    OcrBatchContentArea,
    OcrBatchCounterBadge,
    OcrBatchFileInfo,
    OcrBatchFileList,
    OcrBatchFileListItem,
    OcrBatchFileTypeBadge,
    OcrBatchHeader,
    OcrBatchMainContainer,
    OcrBatchPrimaryButton,
    OcrBatchProcessingSpinner,
    OcrBatchSecondaryButton,
    OcrBatchSelectedCount,
    OcrBatchStatusLabel,
    OcrBatchToolbar,
    OcrBatchToolbarLabel
} from "./OcrBatch.styled";
import {useTranslation} from "react-i18next";
import {Checkbox, CircularProgress, IconButton, Tooltip, Typography} from "@mui/material";
import ExportDropdown from "../../components/ocr-batch/ExportDropdown";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {usePageTitle} from "../../hooks/usePageTitle";
import {PageTitleKeys} from "../../config/routeConstants";
import {formatOcrFileSize} from "../../utils/ocr-utils";
import {OcrFileExportFormat, OcrFileStatus, OcrProcessedFile} from "../../models/ocr.models";
import {useOcrBatchItem} from "../../hooks/useOcr";
import ReplayIcon from '@mui/icons-material/Replay';

const OcrBatchDetails = () => {
    usePageTitle(PageTitleKeys.OcrBatchDetails);

    const {id} = useParams<{id: string}>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {batch, loading, error, exportFiles, retryFileProcessing} = useOcrBatchItem(Number(id));
    const files = batch?.messages[0]?.files ?? [];

    const [selectedFilesToExport, setSelectedFilesToExport] = useState<OcrProcessedFile[]>([]);

    const selectAllRef = useRef<HTMLInputElement>(null);

    const selectableItems = files.filter(item => item.status === 'SUCCESS') || [];

    const allSelected = selectableItems.length > 0 && selectedFilesToExport.length === selectableItems.length;
    const someSelected = selectedFilesToExport.length > 0 && !allSelected;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSelected;
        }
    }, [someSelected]);

    const handleSelectAll = () => {
        allSelected ? setSelectedFilesToExport([]) : setSelectedFilesToExport([...selectableItems]);
    };

    const toggleExportItem = (item: OcrProcessedFile) => {
        if (item.status !== 'SUCCESS') return;

        setSelectedFilesToExport(prev =>
            prev.some(file => file.id === item.id)
                ? prev.filter(file => file.id !== item.id)
                : [...prev, item]
        );
    };

    // TODO: to be implemented in the future
    const handleAddToSources = (event: React.MouseEventHandler<HTMLButtonElement>) => {
        // console.log("To be implemented");
    };

    const handleRetryFileProcessing = (fileId: number) => {
        if (!fileId || !id) {
            console.error('Invalid fileId or batchId');
            return;
        }

        retryFileProcessing(Number(id), fileId);
    };

    const statusMap: Record<OcrFileStatus, string> = {
        PENDING: t('ocr.status.pending'),
        PROCESSING: t('ocr.status.processing'),
        SUCCESS: t('ocr.status.success'),
        ERROR: t('ocr.status.error'),
    };

    if (!id || loading) return <CircularProgress />;

    if (!batch || error) {
        return (
            <OcrBatchCard>
                <OcrBatchMainContainer>
                    <OcrBatchHeader></OcrBatchHeader>
                    <OcrBatchContentArea>
                        <Typography variant="body2">
                            {t('ocr.label.noOcrBatch')}
                        </Typography>

                        <OcrBatchPrimaryButton
                            color="primary"
                            variant="contained"
                            onClick={() => navigate('/ocr/batch')}
                            sx={{ letterSpacing: '1px' }}
                        >
                            {t('ocr.button.createNewOcrBatch')}
                        </OcrBatchPrimaryButton>
                    </OcrBatchContentArea>
                </OcrBatchMainContainer>
            </OcrBatchCard>
        )
    }

    const total = files.length;
    const completed = files.filter(item => item.status === 'SUCCESS').length;

    const statusSortOrder: Record<OcrFileStatus, number> = {
        SUCCESS: 0,
        PROCESSING: 1,
        PENDING: 2,
        ERROR: 3,
    };

    const sortedFiles = [...files].sort(
        (a, b) => statusSortOrder[a.status] - statusSortOrder[b.status]
    );

    return (
        <OcrBatchCard>
            <OcrBatchMainContainer>
                <OcrBatchHeader>
                    <OcrBatchCounterBadge aria-label={t('ocr.aria.counterBadge', {completed: completed, total: total})}>
                        {completed} / {total}
                    </OcrBatchCounterBadge>

                    <OcrBatchBox>
                        <OcrBatchSecondaryButton disabled variant="outlined">
                            {t('ocr.button.addToSources')}
                        </OcrBatchSecondaryButton>

                        <ExportDropdown
                            disabled={selectedFilesToExport.length === 0}
                            files={selectedFilesToExport}
                            onExport={exportFiles}
                            availableFormats={['PDF', 'TXT', 'DOCX'] as OcrFileExportFormat[]}
                            onExportComplete={() => setSelectedFilesToExport([])}
                        />
                    </OcrBatchBox>
                </OcrBatchHeader>

                <OcrBatchToolbar>
                    <Checkbox
                        inputRef={selectAllRef}
                        checked={allSelected}
                        onChange={handleSelectAll}
                        disabled={selectableItems.length === 0}
                        size="small"
                        sx={{ padding: '2px' }}
                        inputProps={{ 'aria-label': t('ocr.aria.checkboxAll') as string }}
                    />

                    <OcrBatchToolbarLabel
                        onClick={handleSelectAll}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleSelectAll()}
                    >
                        {allSelected ? t('ocr.checkbox.uncheckAll') : t('ocr.checkbox.checkAll')}
                    </OcrBatchToolbarLabel>

                    {selectedFilesToExport.length > 0 && (
                        <OcrBatchSelectedCount>
                            {t('ocr.exportDropdown.selected', {count: selectedFilesToExport.length})}
                        </OcrBatchSelectedCount>
                    )}
                </OcrBatchToolbar>

                <OcrBatchContentArea>
                    <OcrBatchFileList role="list">
                        {sortedFiles.map(file => {
                            const isSelectable = file.status === 'SUCCESS';

                            return (
                                <OcrBatchFileListItem role="listitem" key={file.fileName} $clickable={isSelectable}>
                                    <Checkbox
                                        checked={selectedFilesToExport.some(f => f.id === file.id)}
                                        onChange={() => toggleExportItem(file)}
                                        onClick={e => e.stopPropagation()}
                                        disabled={!isSelectable}
                                        size="small"
                                        sx={{ padding: '2px' }}
                                        inputProps={{ 'aria-label': t('ocr.checkbox.checkFileToExport', {fileName: file.fileName}) as string }}
                                    />

                                    <OcrBatchFileTypeBadge aria-hidden="true">
                                        {file.contentType}
                                    </OcrBatchFileTypeBadge>

                                    <OcrBatchFileInfo>
                                        <span className="name"
                                              aria-label={t('ocr.aria.fileName', {name: file.fileName}) as string}>
                                            {file.fileName}
                                        </span>
                                        <span className="size"
                                              aria-label={t('ocr.aria.fileSize', {size: formatOcrFileSize(file.size)}) as string}>
                                            {formatOcrFileSize(file.size)}
                                        </span>
                                    </OcrBatchFileInfo>

                                    {file.status === 'ERROR' && (
                                        <Tooltip title={t('ocr.button.retry')}>
                                            <IconButton color="primary"
                                                        onClick={() => handleRetryFileProcessing(file?.id ?? -1)}
                                                        aria-label={t('ocr.button.retry') as string}
                                            >
                                                <ReplayIcon sx={{ fontSize: 20 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    <OcrBatchStatusLabel
                                        $status={file.status}
                                        aria-live="polite"
                                        aria-label={t('ocr.aria.fileStatus', {status: statusMap[file.status] ?? statusMap.PENDING})}
                                    >
                                        {file.status === 'PROCESSING' &&
                                            <OcrBatchProcessingSpinner aria-hidden="true"/>
                                        }
                                        {statusMap[file.status] ?? statusMap.PENDING}
                                    </OcrBatchStatusLabel>

                                    <Tooltip title={t('ocr.button.viewResults')}>
                                        <IconButton color="primary"
                                                    disabled={file.status !== 'SUCCESS' || (!file || !file?.id)}
                                                    aria-disabled={file.status !== 'SUCCESS' || (!file || !file?.id)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/ocr/batch/${id}/preview/${file.id}`);
                                                    }}
                                                    aria-label={t('ocr.button.viewResults') as string}
                                        >
                                            <ChevronRightIcon sx={{ fontSize: 24 }} />
                                        </IconButton>
                                    </Tooltip>
                                </OcrBatchFileListItem>
                            )}
                        )}
                    </OcrBatchFileList>
                </OcrBatchContentArea>
            </OcrBatchMainContainer>
        </OcrBatchCard>
    );
};

export default OcrBatchDetails;